import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { ComponentProps, memo } from 'react';

import { PracticeItem } from '@oui/app-core/src/components/PracticeItem';
import { Heading, Label, Text } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { useTheme } from '@oui/app-core/src/styles';
import { FragmentOf, graphql, readFragment, ResultOf } from '@oui/lib/src/graphql/tada';

import { TabScreenProps } from '@src/types/navigation';

export const WeeklyPracticeFragment = graphql(
  `
    fragment WeeklyPractice on Query {
      user {
        ID
        role {
          ID
          breathHoldingWidget {
            ID
            name
            iconName
            practiceTileColor
          }
          hyperventilationWidget {
            ID
            name
            iconName
            practiceTileColor
          }
          wordPairingWidget {
            ID
            name
            iconName
            practiceTileColor
          }
        }
      }
      actionTodos {
        todo {
          __typename
          pendingActionID
        }
        completed {
          __typename
          actionID
        }
      }
    }
  `,
  [],
);

const FinishSession1Ribbon = memo(() => {
  const { theme } = useTheme();
  return (
    <LinearGradient
      colors={['rgba(255, 255, 255, 0.0)', theme.color.accent300, 'white']}
      style={[{ height: 40, justifyContent: 'center', alignItems: 'center' }]}
      locations={[0, 0.5, 1]}
      start={[0, 0]}
      end={[1, 0]}
    >
      <Text
        text={'Finish session 1 to start practicing'}
        testID="FinishSession1Ribbon"
        weight="semibold"
        color={theme.color.gray300}
      />
    </LinearGradient>
  );
});

function ActionTodoItem(props: {
  data: ResultOf<typeof WeeklyPracticeFragment>;
  item:
    | ResultOf<typeof WeeklyPracticeFragment>['actionTodos']['todo'][number]
    | ResultOf<typeof WeeklyPracticeFragment>['actionTodos']['completed'][number];
  complete?: boolean;
}) {
  const complete = props.complete || false;
  const { navigate } = useNavigation<TabScreenProps<'Learn'>['navigation']>();
  const item = props.item;

  switch (item.__typename) {
    case 'PendingBreathHoldingAction':
    case 'BreathHoldingAction':
      return (
        <PracticeItem
          complete={complete}
          text={props.data?.user?.role?.breathHoldingWidget?.name!}
          icon={
            props.data?.user?.role?.breathHoldingWidget?.iconName! as ComponentProps<
              typeof PracticeItem
            >['icon']
          }
          color={props.data?.user?.role?.breathHoldingWidget?.practiceTileColor!}
          onPress={() => navigate('StressBreathingIndex', { type: 'holding' })}
        />
      );
    case 'PendingHyperventilationAction':
    case 'HyperventilationAction':
      return (
        <PracticeItem
          complete={complete}
          text={props.data?.user?.role?.hyperventilationWidget?.name!}
          icon={
            props.data?.user?.role?.hyperventilationWidget?.iconName! as ComponentProps<
              typeof PracticeItem
            >['icon']
          }
          color={props.data?.user?.role?.hyperventilationWidget?.practiceTileColor!}
          onPress={() => navigate('StressBreathingIndex', { type: 'hyperventilation' })}
        />
      );
    case 'PendingWordPairingAction':
    case 'WordPairingAction':
      return (
        <PracticeItem
          complete={complete}
          text={props.data?.user?.role?.wordPairingWidget?.name!}
          icon={
            props.data?.user?.role?.wordPairingWidget?.iconName! as ComponentProps<
              typeof PracticeItem
            >['icon']
          }
          color={props.data?.user?.role?.wordPairingWidget?.practiceTileColor!}
          onPress={() => navigate('WordPairingIndex', {})}
        />
      );
    case 'EatingLogAddAction':
    case 'PendingEatingLogAddAction':
    case 'StaticAction':
    case 'PendingThoughtDiaryEntrySwitchAction':
    case 'ThoughtDiaryEntrySwitchAction':
    case 'PendingThoughtDiaryEntrySpotAction':
    case 'ThoughtDiaryEntrySpotAction':
    case 'PendingThoughtDiaryEntryTestAction':
    case 'ThoughtDiaryEntryTestAction':
    case 'PendingCopingCardReviewAction':
    case 'CopingCardReviewAction':
    case 'PendingCopingCardAddAction':
    case 'CopingCardAddAction':
    case 'PendingMyPlanReviewAction':
    case 'MyPlanReviewAction':
    case 'PendingSleepDiaryEntryNightAction':
    case 'PendingSleepDiaryEntryMorningAction':
    case 'SleepDiaryEntryNightAction':
    case 'SleepDiaryEntryMorningAction':
    case 'PendingRelaxAction':
    case 'RelaxAction':
    case 'PendingHopeKitAddAction':
    case 'HopeKitAddAction':
    case 'PendingHopeKitReviewAction':
    case 'HopeKitReviewAction':
    case 'PendingActivityRateAction':
    case 'ActivityRateAction':
    case 'PendingActivityAddAction':
    case 'ActivityAddAction':
      return null;
  }
}

export const WeeklyPractice = ({
  practice,
  completedSession1,
}: {
  practice: FragmentOf<typeof WeeklyPracticeFragment>;
  completedSession1: boolean;
}) => {
  const { theme } = useTheme();
  const practiceData = readFragment(WeeklyPracticeFragment, practice);
  const hasRecords =
    !!practiceData.actionTodos.todo.length || !!practiceData.actionTodos.completed.length;

  return hasRecords || !completedSession1 ? (
    <View
      style={{
        padding: 20,
        paddingTop: 15,
        borderRadius: 20,
        backgroundColor: 'white',
      }}
    >
      <Heading
        level={3}
        text="To-do List"
        style={{
          marginBottom: 25,
        }}
      />
      <View
        style={{
          gap: 50,
        }}
      >
        {practiceData.actionTodos.todo.length || !completedSession1 ? (
          <View
            style={{
              gap: 25,
            }}
          >
            <Label small text="Up next" color={theme.color.gray300} />
            {!completedSession1 ? (
              <View style={{ marginHorizontal: -15 }}>
                <FinishSession1Ribbon />
              </View>
            ) : (
              <View
                testID="WeeklyPractice_todo"
                style={{
                  gap: 15,
                }}
              >
                {practiceData.actionTodos.todo.map((action, index) => (
                  <View
                    key={action.pendingActionID}
                    style={{
                      paddingBottom: index === practiceData.actionTodos.todo.length - 1 ? 0 : 15,
                      borderBottomWidth: index === practiceData.actionTodos.todo.length - 1 ? 0 : 1,
                      borderColor: '#DEE0E5',
                    }}
                  >
                    <ActionTodoItem data={practiceData} item={action} />
                  </View>
                ))}
              </View>
            )}
          </View>
        ) : null}
        {practiceData.actionTodos.completed.length ? (
          <View
            testID="WeeklyPractice_completed"
            style={{
              gap: 25,
            }}
          >
            <Label small text="Done" color={theme.color.gray300} />
            <View>
              {practiceData.actionTodos.completed.map((action, index) => (
                <View
                  key={action.__typename}
                  style={{
                    paddingTop: index === 0 ? 0 : 15,
                    paddingBottom: index === practiceData.actionTodos.completed.length - 1 ? 0 : 15,
                    borderBottomWidth:
                      index === practiceData.actionTodos.completed.length - 1 ? 0 : 1,
                    borderColor: '#DEE0E5',
                  }}
                >
                  <ActionTodoItem data={practiceData} item={action} complete />
                </View>
              ))}
            </View>
          </View>
        ) : null}
      </View>
    </View>
  ) : null;
};
